import documentsData from '@/mockData/documents.json'
import documentTypesData from '@/mockData/documentTypes.json'
import DocumentTypesService from '@/services/api/documentTypes.services'

const state = {
    documents: documentsData,
    documentTypes: documentTypesData,
    isLoading: false,
    employeeDocuments: {}, 
}

const mutations = {
    ADD_DOCUMENT(state, form) {
        state.documents.push(form)
    },
    SET_DOCUMENT_TYPES(state, payload) {
        state.documentTypes = payload
    },
    ADD_DOCUMENT_TYPE(state, form) {
        state.documentTypes.push(form)
    },
    UPDATE_DOCUMENT_TYPE(state, { id, ...payload }) {
        const index = state.documentTypes.findIndex((dt) => dt.id === id)
        if (index !== -1) {
            state.documentTypes[index] = {
                ...state.documentTypes[index],
                ...payload,
            }
        }
    },
    DELETE_DOCUMENT_TYPE(state, id) {
        state.documentTypes = state.documentTypes.filter((dt) => dt.id !== id)
    },
    SET_LOADING(state, payload) {
        state.isLoading = payload
    },
    SET_EMPLOYEE_DOCUMENTS(state, { employeeId, documents }) {
        state.employeeDocuments = {
            ...state.employeeDocuments,
            [employeeId]: documents,
        }
    },
    ADD_EMPLOYEE_DOCUMENT(state, { employeeId, document }) {
        if (!state.employeeDocuments[employeeId]) {
            state.employeeDocuments[employeeId] = []
        }
        state.employeeDocuments[employeeId].push(document)
    },
    DELETE_EMPLOYEE_DOCUMENT(state, { employeeId, documentId }) {
        if (state.employeeDocuments[employeeId]) {
            state.employeeDocuments[employeeId] = state.employeeDocuments[
                employeeId
            ].filter((doc) => doc.id !== documentId)
        }
    },
}

const actions = {
    addDocument({ commit }, form) {
        commit('ADD_DOCUMENT', form)
    },

    async fetchDocumentTypes({ commit }) {
        commit('SET_LOADING', true)
        try {
            const response = await DocumentTypesService.get('documentTypes')
            commit('SET_DOCUMENT_TYPES', response.data.data || [])
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async createDocumentType({ commit }, documentType) {
        commit('SET_LOADING', true)
        try {
            const response = await DocumentTypesService.post('documentTypes', {}, documentType)
            commit('ADD_DOCUMENT_TYPE', response.data.data)
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async updateDocumentType({ commit }, { id, ...payload }) {
        commit('SET_LOADING', true)
        try {
            const response = await DocumentTypesService.put('documentType', id, payload)
            commit('UPDATE_DOCUMENT_TYPE', { id, ...response.data.data })
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async deleteDocumentType({ commit }, id) {
        commit('SET_LOADING', true)
        try {
            const response = await DocumentTypesService.delete('documentType', id)
            commit('DELETE_DOCUMENT_TYPE', id)
            return response
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async uploadDocument(
        { commit, state },
        { employeeId, documentTypeId, file },
    ) {
        commit('SET_LOADING', true)
        try {
            await new Promise((resolve) => setTimeout(resolve, 1000)) //!remove

            const documentId = Date.now()
            const document = {
                id: documentId,
                employeeId,
                documentTypeId,
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                uploadedAt: new Date().toISOString(),
                file: file,
            }
            commit('ADD_EMPLOYEE_DOCUMENT', { employeeId, document })
            return Promise.resolve({ data: { data: document } })
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async getEmployeeDocuments({ commit, state }, employeeId) {
        commit('SET_LOADING', true)
        try {
            const documents = state.employeeDocuments[employeeId] || []
            return Promise.resolve({ data: { data: documents } })
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async deleteDocument({ commit }, { employeeId, documentId }) {
        commit('SET_LOADING', true)
        try {
            commit('DELETE_EMPLOYEE_DOCUMENT', { employeeId, documentId })
            return Promise.resolve({ data: { success: true } })
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('SET_LOADING', false)
        }
    },
}

const getters = {
    getData: (state) => state.documents,
    getTypes: (state) => state.documentTypes,
    getDocumentTypes: (state) => state.documentTypes,
    isLoading: (state) => state.isLoading,
    getEmployeeDocuments: (state) => (employeeId) => {
        return state.employeeDocuments[employeeId] || []
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
